/**
 ChainVisualizationRelativityCalculator - Utility for calculating dynamic relativity values
 
 This utility calculates relativity percentages for sellout steps based on a selected anchor track.
 The anchor track becomes the 100% reference, and all other tracks' relativity values are calculated
 proportionally based on their sellout prices.
 */

/**
 Extracts all available tracks from chain data that have sellout steps (step5)
 */
export const extractAvailableAnchors = (chainData) => {
  if (!chainData || !chainData.topologySubsections) {
    return [];
  }

  const anchors = [];

  chainData.topologySubsections.forEach((subsection) => {
    subsection.tracks.forEach((track) => {
      // Check if track has sellout steps (step5)
      const hasSelloutStep = track.steps.some((step) => step.type === "step5");

      if (hasSelloutStep) {
        anchors.push({
          id: track.id,
          label: track.label,
          subsectionName: subsection.name,
        });
      }
    });
  });

  return anchors;
};

/*
Gets the sellout price for a specific track
 */
export const getSelloutPriceForTrack = (chainData, trackId) => {
  if (!chainData || !chainData.topologySubsections) {
    return null;
  }

  for (const subsection of chainData.topologySubsections) {
    for (const track of subsection.tracks) {
      if (track.id === trackId) {
        const selloutStep = track.steps.find((step) => step.type === "step5");
        return selloutStep?.metrics?.price || null;
      }
    }
  }

  return null;
};

/*
 Calculates relativity values for all tracks based on a selected anchor
 */
export const calculateRelativityValues = (chainData, anchorTrackId) => {
  const anchorPrice = getSelloutPriceForTrack(chainData, anchorTrackId);

  if (!anchorPrice) {
    console.warn(
      `Could not find sellout price for anchor track: ${anchorTrackId}`,
    );
    return {};
  }

  const relativityMap = {};

  chainData.topologySubsections.forEach((subsection) => {
    subsection.tracks.forEach((track) => {
      const trackPrice = getSelloutPriceForTrack(chainData, track.id);

      if (trackPrice) {
        // Calculate relativity as percentage relative to anchor
        // Formula: (trackPrice / anchorPrice) * 100
        const relativity = (trackPrice / anchorPrice) * 100;
        relativityMap[track.id] = Number(relativity.toFixed(1));
      }
    });
  });

  return relativityMap;
};

/**
 * Updates chain data with calculated relativity values
 */
export const updateChainDataWithRelativity = (chainData, anchorTrackId) => {
  const relativityMap = calculateRelativityValues(chainData, anchorTrackId);

  // Deep clone the data to avoid mutations
  const updatedData = JSON.parse(JSON.stringify(chainData));

  updatedData.topologySubsections.forEach((subsection) => {
    subsection.tracks.forEach((track) => {
      track.steps.forEach((step) => {
        if (step.type === "step5" && relativityMap[track.id] !== undefined) {
          step.relativity = relativityMap[track.id];
        }
      });
    });
  });

  return updatedData;
};

/**
 * Gets a friendly display name for an anchor track
 */
export const getAnchorDisplayName = (chainData, trackId) => {
  if (!chainData || !chainData.topologySubsections) {
    return trackId;
  }

  for (const subsection of chainData.topologySubsections) {
    for (const track of subsection.tracks) {
      if (track.id === trackId) {
        return `${track.label} (${subsection.name})`;
      }
    }
  }

  return trackId;
};

/**
 * Validates that a track can be used as an anchor (has sellout steps)
 */
export const isValidAnchor = (chainData, trackId) => {
  const selloutPrice = getSelloutPriceForTrack(chainData, trackId);
  return selloutPrice !== null && selloutPrice > 0;
};

export default {
  extractAvailableAnchors,
  getSelloutPriceForTrack,
  calculateRelativityValues,
  updateChainDataWithRelativity,
  getAnchorDisplayName,
  isValidAnchor,
};
